'use client';

import {FC, startTransition, SyntheticEvent, useMemo} from 'react';
import {Box, Container, MenuItem, SelectChangeEvent, Typography, useMediaQuery, useTheme} from '@mui/material';
import {usePathname, useRouter, useSearchParams} from 'next/navigation';
import {ToggleContentSec} from '@/app/[locale]/[[...slug]]/types';
import {topicHasTopicAndLabel} from '@/lib/type-guards';
import {RichTextRenderer} from '@/components/contentful/RichTextRenderer';
import {Document} from '@contentful/rich-text-types';
import ContentfulImage from '@/components/ui/image/ContentfulImage';
import {
  StyledToggleContentSectionContainer,
  StyledToggleContentTabs,
  StyledToggleContentTab,
  StyledToggleContentItemContainer,
  StyledToggleContentMediaContainer,
  StyledToggleContentSelect,
  StyledToggleContentHeading,
} from '@/components/sections/toggle-content-section/ToggleContentSection.styles';
import {ToggleContentItem} from '@/components/sections/toggle-content-section/actions';
import {asVariant} from '@/lib/typography-variant';

type ToggleContentSectionProps = {
  section: ToggleContentSec;
  prefetchedContentMap?: Record<string, ToggleContentItem[]>;
};

function a11yProps(value: string) {
  return {
    id: `toggle-content-tab-${value}`,
    'aria-controls': `toggle-content-tabpanel-${value}`,
  };
}

export const ToggleContentSection: FC<ToggleContentSectionProps> = ({section, prefetchedContentMap = {}}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const topics = useMemo(() => {
    return section.topicsCollection?.items ?? [];
  }, [section.topicsCollection]);

  const validTopics = useMemo(
    () => topics.filter(topicHasTopicAndLabel),
    [topics]
  );

  const validTopicValues = useMemo(
    () => new Set(validTopics.map((t) => t.topic)),
    [validTopics]
  );

  const defaultTopic = validTopics.length > 0 ? validTopics[0].topic : '';

  const categoryFromQueryRaw = searchParams?.get('topic');
  const categoryFromQuery =
    categoryFromQueryRaw && validTopicValues.has(categoryFromQueryRaw)
      ? categoryFromQueryRaw
      : defaultTopic;

  const selected = categoryFromQuery;

  const contents = useMemo(
    () => section.contentsCollection?.items ?? [],
    [section.contentsCollection]
  );

  const filteredContents = useMemo(
    () => contents.filter((item) => item?.topic?.topic === selected),
    [contents, selected]
  );

  const updateSelection = (newValue: string) => {
    const params = new URLSearchParams(searchParams?.toString());
    params.set('topic', newValue);

    startTransition(() => {
      router.replace(`${pathname}?${params.toString()}`, {
        scroll: false,
      });
    });
  };

  const handleTabChange = (_: SyntheticEvent, newValue: string) => {
    updateSelection(newValue);
  };

  const handleSelectChange = (e: SelectChangeEvent<unknown>) => {
    updateSelection(e.target.value as string);
  };
  
  return (
    <Container maxWidth='mdlg' {...(section.id && {id: section.id})}>
      <StyledToggleContentSectionContainer>
        {section.heading && (
          <StyledToggleContentHeading variant='h2'>
            {section.heading}
          </StyledToggleContentHeading>
        )}
        {validTopics.length > 0 && (
          <Box sx={{marginBottom: 5, width: '100%'}}>
            {isSmallScreen ? (
              <StyledToggleContentSelect
                value={selected}
                onChange={handleSelectChange}
              >
                {validTopics.map((topic) => (
                  <MenuItem key={topic.sys.id} value={topic.topic}>
                    {topic.label}
                  </MenuItem>
                ))}
              </StyledToggleContentSelect>
            ) : (
              <StyledToggleContentTabs
                value={selected}
                onChange={handleTabChange}
                aria-label='Toggle content tabs'
              >
                {validTopics.map((topic) => (
                  <StyledToggleContentTab
                    key={topic.sys.id}
                    value={topic.topic}
                    label={topic.label}
                    {...a11yProps(topic.topic)}
                  />
                ))}
              </StyledToggleContentTabs>
            )}
          </Box>
        )}

        {filteredContents.map((item, i) => {
          if (!item?.sys?.id) return null;
          const contentItems = prefetchedContentMap[item.sys.id];
          if (!contentItems) return null;

          return contentItems.map((content, j) => {
            if (!content) return null;

            if (content.__typename === 'TwoColumnRichText') {
              return (
                <StyledToggleContentItemContainer key={`${selected}-${i}-${j}`}>
                  {content.leftColumn && (
                    <Box sx={{flex: 1}}>
                      <RichTextRenderer
                        richTextDocument={content.leftColumn.json as unknown as Document}
                      />
                    </Box>
                  )}
                  {content.rightColumn && (
                    <Box sx={{flex: 1}}>
                      <RichTextRenderer
                        richTextDocument={content.rightColumn.json as unknown as Document}
                      />
                    </Box>
                  )}
                </StyledToggleContentItemContainer>
              );
            }

            if (content.__typename === 'InformationMediaSection') {
              return (
                <StyledToggleContentItemContainer key={`${selected}-${i}-${j}`}>
                  <Box sx={{flex: 1}}>
                    {content.heading && (
                      <Typography variant={asVariant(content.headingSize, 'h2')} sx={{marginBottom: 2}}>
                        {content.heading}
                      </Typography>
                    )}
                    {content.breadText && (
                      <RichTextRenderer
                        richTextDocument={content.breadText.json as unknown as Document}
                      />
                    )}
                  </Box>

                  {content.media?.url && (
                    <Box sx={{flex: 1, width: '100%', display: 'flex'}}>
                      <StyledToggleContentMediaContainer>
                        <ContentfulImage
                          src={content.media.url}
                          alt={content.heading ?? ''}
                          fill
                          sizes='(max-width: 600px) 100vw, 50vw'
                          style={{objectFit: 'cover'}}
                          quality={85}
                        />
                      </StyledToggleContentMediaContainer>
                    </Box>
                  )}
                </StyledToggleContentItemContainer>
              );
            }

            return null;
          });
        })}
      </StyledToggleContentSectionContainer>
    </Container>
  );
};